import {
  ArrowLeftOutlined,
  BranchesOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  DatabaseOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  SearchOutlined,
  ShareAltOutlined,
  TableOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { Alert, Button, Input, Select, Spin } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LineageGraph } from './LineageGraph';
import { LineageAiInspector } from './LineageAiInspector';
import { LineageDemoCoach } from './LineageDemoCoach';
import { LineageHelpDrawer } from './LineageHelpDrawer';
import { LineageScanModal } from './LineageScanModal';
import { lineageDemoApi, LineageAppSummary, LineageEdge, LineageEvidence, LineageFieldTrace, LineageNode, LineageProjection, LineageStatus } from './lineageDemoApi';

const { Option } = Select;
const defaultAppId = 'app-consumer-sales-bm';

const providerLabel: Record<LineageEvidence['provider'], string> = {
  'qlik-lineage-api': 'Qlik Lineage API',
  'load-script': 'Load script',
  engine: 'Qlik Engine',
  'reload-log': 'Reload log',
};

export const LineageCenterPage: React.FC = () => {
  const [apps, setApps] = useState<LineageAppSummary[]>([]);
  const [appSearch, setAppSearch] = useState('');
  const [selectedAppId, setSelectedAppId] = useState(defaultAppId);
  const [projection, setProjection] = useState<LineageProjection | null>(null);
  const [selectedNode, setSelectedNode] = useState<LineageNode | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<LineageEdge | null>(null);
  const [fieldTrace, setFieldTrace] = useState<LineageFieldTrace | null>(null);
  const [selectedField, setSelectedField] = useState<string | null>(null);
  const [fieldTraceLoading, setFieldTraceLoading] = useState(false);
  const [evidence, setEvidence] = useState<LineageEvidence[]>([]);
  const [depth, setDepth] = useState<'resource' | 'table' | 'field'>('field');
  const [scope, setScope] = useState<'both' | 'upstream' | 'downstream'>('both');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<LineageStatus | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [inspectorView, setInspectorView] = useState<'details' | 'ai' | 'demo'>('ai');
  const fieldTraceRequest = useRef(0);

  const inspectNode = useCallback(async (node: LineageNode) => {
    const requestId = ++fieldTraceRequest.current;
    setSelectedNode(node);
    setInspectorView('ai');
    setSelectedEdge(null);
    setEvidence([]);
    setSelectedField(null);
    setFieldTrace(null);
    setFieldTraceLoading(true);
    try {
      const [detail, trace] = await Promise.all([lineageDemoApi.node(node.id), lineageDemoApi.fieldTrace(node.id)]);
      if (requestId !== fieldTraceRequest.current) return;
      setSelectedNode(detail.node);
      setEvidence(detail.evidence);
      setFieldTrace(trace);
    } catch (requestError) {
      if (requestId === fieldTraceRequest.current) setError(requestError instanceof Error ? requestError.message : String(requestError));
    } finally { if (requestId === fieldTraceRequest.current) setFieldTraceLoading(false); }
  }, []);

  const traceField = useCallback(async (field: string | null) => {
    if (!selectedNode) return;
    const requestId = ++fieldTraceRequest.current;
    setSelectedField(field);
    setInspectorView('ai');
    setFieldTraceLoading(true);
    try {
      const trace = await lineageDemoApi.fieldTrace(selectedNode.id, field);
      if (requestId === fieldTraceRequest.current) setFieldTrace(trace);
    } catch (requestError) {
      if (requestId === fieldTraceRequest.current) setError(requestError instanceof Error ? requestError.message : String(requestError));
    } finally { if (requestId === fieldTraceRequest.current) setFieldTraceLoading(false); }
  }, [selectedNode]);

  const loadApp = useCallback(async (appId: string) => {
    setLoading(true);
    setError(null);
    try {
      const next = await lineageDemoApi.appOverview(appId);
      setSelectedAppId(appId);
      setProjection(next);
      setStatus(next.status);
      setScope('both');
      const appNode = next.nodes.find(node => node.id === appId) ?? next.nodes[0];
      if (appNode) await inspectNode(appNode);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : String(requestError));
    } finally {
      setLoading(false);
    }
  }, [inspectNode]);

  useEffect(() => {
    let active = true;
    void Promise.all([lineageDemoApi.listApps(), lineageDemoApi.status(), lineageDemoApi.appOverview(defaultAppId)]).then(async ([nextApps, nextStatus, nextProjection]) => {
      if (!active) return;
      setApps(nextApps);
      setStatus(nextStatus);
      setProjection(nextProjection);
      const appNode = nextProjection.nodes.find(node => node.id === defaultAppId) ?? nextProjection.nodes[0];
      if (appNode) await inspectNode(appNode);
    }).catch(requestError => {
      if (active) setError(requestError instanceof Error ? requestError.message : String(requestError));
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [inspectNode]);

  const inspectEdge = useCallback(async (edge: LineageEdge) => {
    setSelectedEdge(edge);
    setSelectedNode(null);
    setInspectorView('details');
    setEvidence([]);
    try {
      const detail = await lineageDemoApi.edge(edge.id);
      setSelectedEdge(detail.edge);
      setEvidence(detail.evidence);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : String(requestError));
    }
  }, []);

  const exploreSelected = async () => {
    if (!selectedNode) return;
    setLoading(true);
    setError(null);
    try {
      const next = await lineageDemoApi.explore(selectedNode.id, selectedAppId);
      setProjection(next);
      setStatus(next.status);
      setScope('both');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : String(requestError));
    } finally { setLoading(false); }
  };

  const refreshAfterScan = useCallback(async () => {
    setLoading(true);
    try {
      const [nextStatus, nextApps, nextProjection] = await Promise.all([lineageDemoApi.status(), lineageDemoApi.listApps(), lineageDemoApi.appOverview(selectedAppId)]);
      setStatus(nextStatus);
      setApps(nextApps);
      setProjection(nextProjection);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : String(requestError));
    } finally { setLoading(false); }
  }, [selectedAppId]);

  const visibleApps = useMemo(() => apps.filter(app => `${app.name} ${app.model}`.toLowerCase().includes(appSearch.toLowerCase())), [apps, appSearch]);

  const scopedProjection = useMemo(() => {
    if (!projection || scope === 'both') return projection;
    const edgesByTarget = new Map<string, LineageEdge[]>();
    const edgesBySource = new Map<string, LineageEdge[]>();
    projection.edges.forEach(edge => {
      edgesByTarget.set(edge.target, [...(edgesByTarget.get(edge.target) ?? []), edge]);
      edgesBySource.set(edge.source, [...(edgesBySource.get(edge.source) ?? []), edge]);
    });
    const keep = new Set<string>([projection.anchorNodeId]);
    const queue = [projection.anchorNodeId];
    while (queue.length) {
      const id = queue.shift();
      if (!id) continue;
      const nextEdges = scope === 'upstream' ? edgesByTarget.get(id) ?? [] : edgesBySource.get(id) ?? [];
      nextEdges.forEach(edge => {
        const nextId = scope === 'upstream' ? edge.source : edge.target;
        if (!keep.has(nextId)) { keep.add(nextId); queue.push(nextId); }
      });
    }
    return { ...projection, nodes: projection.nodes.filter(node => keep.has(node.id)), edges: projection.edges.filter(edge => keep.has(edge.source) && keep.has(edge.target)) };
  }, [projection, scope]);

  const visibleTraceSummary = useMemo(() => {
    const visibleSourceIds = new Set((scopedProjection?.nodes ?? []).filter(node => node.type === 'source').map(node => node.id));
    return {
      tracedSources: (fieldTrace?.sourceNodeIds ?? []).filter(id => visibleSourceIds.has(id)).length,
      sources: visibleSourceIds.size,
    };
  }, [fieldTrace, scopedProjection]);

  return (
    <section className="lineage-center-page">
      <aside className="lineage-app-rail">
        <h2>Consumer Apps</h2>
        <Input aria-label="Search QVF or model" placeholder="Search QVF or model" prefix={<SearchOutlined />} value={appSearch} onChange={event => setAppSearch(event.target.value)} />
        <div className="lineage-app-list">
          {visibleApps.map(app => (
            <button type="button" key={app.id} className={app.id === selectedAppId ? 'is-selected' : ''} onClick={() => void loadApp(app.id)}>
              <FileTextOutlined />
              <span><strong>{app.name}</strong><small>{app.model} · {app.qvdStages} QVD stages</small>{app.id === selectedAppId ? <em>Selected App</em> : app.sharedSource ? <em className="is-shared">Shared source</em> : null}</span>
            </button>
          ))}
        </div>
        {status ? (
          <div className="lineage-snapshot-card">
            <span><ClockCircleOutlined /> Client-managed on-prem snapshot</span>
            <strong>{new Date(status.generatedAt).toLocaleString()}</strong>
            <small>{status.scanSummary.scannedApps} published Apps · {status.scanSummary.simulatedUpstreamNodes} demo upstream nodes</small>
            <Button size="small" icon={<FileSearchOutlined />} onClick={() => setScanOpen(true)}>View site scan</Button>
          </div>
        ) : null}
      </aside>

      <div className="lineage-workbench">
        <header className="lineage-page-header">
          <div><span>LINEAGE INTELLIGENCE</span><h1>Lineage Center</h1><p>Select a consuming App, then recenter the graph from any source, QVD, App, or analytic node.</p></div>
          <div className="lineage-page-header-actions">
            <Button className="lineage-help-trigger" aria-label="Open Lineage Center help" icon={<QuestionCircleOutlined />} onClick={() => setHelpOpen(true)}>Help</Button>
          </div>
        </header>
        {error ? <Alert className="lineage-error" type="error" showIcon message="Lineage catalog unavailable" description={error} closable onClose={() => setError(null)} /> : null}
        {projection ? (
          <>
            <div className="lineage-toolbar">
              <div>
                {projection.mode === 'node-explore' ? <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => void loadApp(selectedAppId)}>Back to selected App</Button> : null}
                <h2>{projection.mode === 'node-explore' ? `Explore · ${projection.breadcrumb[projection.breadcrumb.length - 1]?.label}` : projection.nodes.find(node => node.id === selectedAppId)?.label}</h2>
                <p>{projection.summary.sources} sources · {projection.summary.qvdStages} QVD stages · {projection.summary.fields} source fields · {projection.summary.analytics} analytics</p>
                <div className="lineage-provenance-legend"><span className="is-observed">Site observed</span><span className="is-parsed">Script parsed</span><span className="is-demo">Simulated upstream</span></div>
              </div>
              <div className="lineage-toolbar-actions">
                <Select aria-label="Lineage boundary" value={scope} onChange={setScope} suffixIcon={<BranchesOutlined />}>
                  <Option value="both">Upstream + downstream</Option><Option value="upstream">Upstream only</Option><Option value="downstream">Downstream only</Option>
                </Select>
                <div className="lineage-view-tabs"><button className="is-active">Overview</button><button disabled>Lineage</button><button disabled>Impact</button></div>
                <div className="lineage-depth-tabs"><button className={depth === 'resource' ? 'is-active' : ''} onClick={() => setDepth('resource')}>Resource</button><button className={depth === 'table' ? 'is-active' : ''} onClick={() => setDepth('table')}>Table</button><button className={depth === 'field' ? 'is-active' : ''} onClick={() => setDepth('field')}>Field</button></div>
              </div>
            </div>
            <div className="lineage-topology-wrap">
              {loading ? <div className="lineage-loading"><Spin /><span>Projecting persistent lineage graph…</span></div> : null}
              {depth === 'field' && fieldTrace ? (
                <div className={`lineage-trace-legend ${fieldTrace.selectedField ? 'is-field-focus' : ''} ${fieldTraceLoading ? 'is-updating' : ''}`} aria-live="polite">
                  <CheckCircleFilled />
                  <span><strong>{fieldTrace.selectedField ?? `${fieldTrace.tracedFields.length} fields`} verified path</strong><small>{visibleTraceSummary.tracedSources} of {visibleTraceSummary.sources} visible sources carry used data · context remains visible</small></span>
                  {fieldTraceLoading ? <Spin size="small" /> : null}
                </div>
              ) : null}
              {scopedProjection ? <LineageGraph key={`${scopedProjection.anchorNodeId}-${scope}`} projection={scopedProjection} selectedNodeId={selectedNode?.id ?? ''} selectedEdgeId={selectedEdge?.id ?? null} fieldTrace={fieldTrace} depth={depth} onSelectNode={node => void inspectNode(node)} onSelectEdge={edge => void inspectEdge(edge)} /> : null}
            </div>
          </>
        ) : <div className="lineage-loading"><Spin /><span>Loading persistent lineage catalog…</span></div>}
      </div>

      <aside className="lineage-detail-panel">
        {selectedNode ? (
          <>
            <span className="lineage-detail-kicker">SELECTED {selectedNode.type.toUpperCase()}</span>
            <div className={`lineage-detail-icon is-${selectedNode.type}`}>{selectedNode.type === 'app' ? <FileTextOutlined /> : selectedNode.type === 'analytic' ? <TableOutlined /> : <DatabaseOutlined />}</div>
            <h2>{selectedNode.label}</h2>
            <p>{selectedNode.subtitle}</p>
            <span className={`lineage-provenance-pill is-${selectedNode.provenance ?? 'declared-demo'}`}>{selectedNode.provenance === 'observed' ? 'Site observed' : selectedNode.provenance === 'parsed' ? 'Script parsed' : 'Simulated upstream'}</span>
            <nav className="lineage-inspector-tabs" aria-label="Lineage inspector view">
              <button type="button" className={inspectorView === 'details' ? 'is-active' : ''} onClick={() => setInspectorView('details')}>Details</button>
              <button type="button" className={inspectorView === 'ai' ? 'is-active' : ''} onClick={() => setInspectorView('ai')}>AI Insight</button>
              <button type="button" className={inspectorView === 'demo' ? 'is-active' : ''} onClick={() => setInspectorView('demo')}>Demo</button>
            </nav>
            {inspectorView === 'details' ? <div className="lineage-inspector-view is-details">
              <dl>
                <dt>Stage</dt><dd>{selectedNode.stage === 'source' || selectedNode.stage === 'analytic' ? selectedNode.stage : `${selectedNode.stage} architecture`}</dd>
                {Object.entries(selectedNode.metadata).filter(([key]) => key !== 'displayOrder').map(([key, value]) => <React.Fragment key={key}><dt>{key}</dt><dd>{String(value)}</dd></React.Fragment>)}
              </dl>
              <div className="lineage-field-list is-interactive">
                <strong><TableOutlined /> Trace fields ({selectedNode.fields.length}) {fieldTraceLoading ? <Spin size="small" /> : null}</strong>
                <small>Select one field to isolate its verified upstream path.</small>
                <div>
                  <button type="button" className={selectedField === null ? 'is-active' : ''} aria-pressed={selectedField === null} aria-label="Trace all fields" onClick={() => void traceField(null)}>All fields</button>
                  {selectedNode.fields.map(field => <button type="button" key={field} className={selectedField === field ? 'is-active' : ''} aria-pressed={selectedField === field} aria-label={`Trace field ${field}`} onClick={() => void traceField(field)}>{field}</button>)}
                </div>
              </div>
              <Button type="primary" block icon={<ShareAltOutlined />} onClick={() => void exploreSelected()}>Explore from here</Button>
              <div className="lineage-evidence-list">
                <strong><CheckCircleFilled /> Evidence ({evidence.length})</strong>
                {evidence.map(item => <article key={item.id}><span>{providerLabel[item.provider]}</span><p>{item.statement}</p><code>{item.locator}</code><small>{item.verification === 'declared-demo' ? 'Simulated observation' : 'Verified observation'}</small></article>)}
              </div>
            </div> : null}
            <div className={`lineage-inspector-view is-ai ${inspectorView === 'ai' ? '' : 'is-hidden'}`}><LineageAiInspector node={selectedNode} field={selectedField} onFieldChange={field => void traceField(field)} /></div>
            {inspectorView === 'demo' ? <div className="lineage-inspector-view is-demo"><LineageDemoCoach /></div> : null}
          </>
        ) : selectedEdge ? (
          <>
            <span className="lineage-detail-kicker">SELECTED RELATIONSHIP</span>
            <div className="lineage-detail-icon is-edge"><ShareAltOutlined /></div>
            <h2>{selectedEdge.relation}</h2>
            <p>{selectedEdge.source} → {selectedEdge.target}</p>
            <span className={`lineage-provenance-pill is-${selectedEdge.provenance ?? 'declared-demo'}`}>{selectedEdge.provenance === 'observed' ? 'Site observed' : selectedEdge.provenance === 'parsed' ? 'Script parsed' : 'Simulated upstream'}</span>
            <div className="lineage-field-list"><strong><FileSearchOutlined /> Field mapping</strong><div>{selectedEdge.fieldMappings.map((mapping, index) => <span key={`${mapping.source}-${index}`}>{mapping.source} → {mapping.target}{mapping.expression ? ` · ${mapping.expression}` : ''}</span>)}</div></div>
            <div className="lineage-evidence-list"><strong><CheckCircleFilled /> Evidence ({evidence.length})</strong>{evidence.map(item => <article key={item.id}><span>{providerLabel[item.provider]}</span><p>{item.statement}</p><code>{item.locator}</code></article>)}</div>
          </>
        ) : <p>Select a node or relationship to inspect its canonical detail and evidence.</p>}
      </aside>
      <LineageHelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} />
      <LineageScanModal open={scanOpen} onClose={() => setScanOpen(false)} onComplete={refreshAfterScan} />
    </section>
  );
};
